import { useState } from "react";
import { useLocation, Link } from "wouter";
import {
  LayoutDashboard,
  Briefcase,
  DollarSign,
  MoreHorizontal,
  Clock,
  FileText,
  Trophy,
  UsersRound,
  Settings,
  Rocket,
  ChevronRight,
  Sun,
  Moon,
  LogOut,
  SquareCheckBig,
  type LucideIcon,
} from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { DuckMark } from "@/components/ducks/duck-mark";
import { useAuth } from "@/hooks/use-auth";
import { useDucksClients } from "@/hooks/use-ducks";
import { useTheme } from "@/hooks/use-theme";
import { isDucksRoute } from "@/lib/ducks";

type PrimaryItem = { href: string; label: string; icon: LucideIcon; flag?: "tasks" };

const PRIMARY: PrimaryItem[] = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/clients", label: "Clients", icon: Briefcase },
  { href: "/tasks", label: "Tasks", icon: SquareCheckBig, flag: "tasks" },
  { href: "/payments", label: "Payments", icon: DollarSign },
];

// The bottom bar is a grid of primary items + the "More" button. Keep the
// column count in step with how many primary items are actually shown so the
// row stays evenly spaced when a flagged item (Tasks) is hidden.
const GRID_COLS: Record<number, string> = {
  4: "grid-cols-4",
  5: "grid-cols-5",
};

type Tone = "amber" | "indigo" | "primary" | "emerald" | "slate";

type OverflowItem = {
  href: string;
  label: string;
  icon: LucideIcon | ((props: { size?: number; strokeWidth?: number }) => JSX.Element);
  tone: Tone;
};

// The bottom bar is full, so Ducks in a Row leads the drawer rather than
// squeezing a sixth column in.
const DUCKS_ITEM: OverflowItem = { href: "/ducks", label: "Ducks in a Row", icon: DuckMark, tone: "amber" };

const OVERFLOW: OverflowItem[] = [
  { href: "/overtime-requests", label: "Overtime Requests", icon: Clock, tone: "amber" },
  { href: "/missions", label: "Epic Missions", icon: Rocket, tone: "primary" },
  { href: "/legal-documents", label: "Legal Documents", icon: FileText, tone: "indigo" },
  { href: "/referral-leaderboard", label: "Leaderboard", icon: Trophy, tone: "primary" },
  { href: "/community", label: "Community", icon: UsersRound, tone: "emerald" },
  { href: "/settings", label: "Settings", icon: Settings, tone: "slate" },
];

const TONE_TILE: Record<Tone, string> = {
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
  indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400",
  primary: "bg-primary/10 text-primary",
  emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
  slate: "bg-slate-100 text-slate-500 dark:bg-slate-700/40 dark:text-slate-300",
};

export default function MobileNav() {
  const [location, setLocation] = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { logoutMutation, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const primary = PRIMARY.filter(
    (r) => r.flag !== "tasks" || user?.tasks_enabled,
  );
  const gridCols = GRID_COLS[primary.length + 1] ?? "grid-cols-5";

  const { data: ducksClients } = useDucksClients();
  const overflow = ducksClients?.length ? [DUCKS_ITEM, ...OVERFLOW] : OVERFLOW;

  const overflowActive = OVERFLOW.some((r) => r.href === location) || isDucksRoute(location);
  const moreActive = overflowActive || moreOpen;

  const navigate = (href: string) => {
    setMoreOpen(false);
    setLocation(href);
  };

  return (
    <>
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 grid ${gridCols} bg-card border-t border-border pb-[22px] shadow-[0_-2px_14px_rgba(15,23,42,0.04)]`}>
        {primary.map((route) => {
          const isActive = route.href === location;
          const Icon = route.icon;
          return (
            <Link key={route.href} href={route.href}>
              <a
                className={`relative flex flex-col items-center justify-center gap-1 pt-[9px] pb-0.5 ${
                  isActive ? "text-primary" : "text-slate-400"
                }`}
              >
                {isActive && (
                  <span className="absolute top-0 h-[3px] w-[26px] rounded-full bg-primary" />
                )}
                <Icon size={22} strokeWidth={isActive ? 2.3 : 2} />
                <span className={`text-[10.5px] ${isActive ? "font-bold" : "font-semibold"}`}>
                  {route.label}
                </span>
              </a>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={`relative flex flex-col items-center justify-center gap-1 pt-[9px] pb-0.5 ${
            moreActive ? "text-primary" : "text-slate-400"
          }`}
        >
          {moreActive && (
            <span className="absolute top-0 h-[3px] w-[26px] rounded-full bg-primary" />
          )}
          <MoreHorizontal size={22} strokeWidth={moreActive ? 2.3 : 2} />
          <span className={`text-[10.5px] ${moreActive ? "font-bold" : "font-semibold"}`}>
            More
          </span>
        </button>
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen} shouldScaleBackground={false}>
        <DrawerContent className="rounded-t-[24px] border-border lg:hidden">
          <DrawerTitle className="sr-only">More navigation</DrawerTitle>
          <div className="px-3 pb-[26px] pt-2">
            <div className="flex flex-col">
              {overflow.map((route) => {
                const Icon = route.icon;
                return (
                  <button
                    key={route.href}
                    type="button"
                    onClick={() => navigate(route.href)}
                    className="flex h-[52px] items-center gap-3 rounded-xl px-3 text-left transition-colors hover:bg-muted/60 active:bg-muted"
                  >
                    <span
                      className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${TONE_TILE[route.tone]}`}
                    >
                      <Icon size={18} strokeWidth={2.1} />
                    </span>
                    <span className="flex-1 text-[15px] font-semibold text-foreground">
                      {route.label}
                    </span>
                    <ChevronRight size={18} className="text-slate-400" />
                  </button>
                );
              })}
            </div>

            <div className="my-2 border-t border-border" />

            <div className="flex gap-2 px-1">
              <button
                type="button"
                onClick={toggleTheme}
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-border bg-card py-2.5 text-[13.5px] font-semibold text-foreground transition-colors hover:bg-muted/60"
              >
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                {theme === "dark" ? "Light mode" : "Dark mode"}
              </button>
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-rose-200 bg-rose-50 py-2.5 text-[13.5px] font-semibold text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
